import React, { useCallback, useEffect, useRef } from 'react'
import { useSearchParam } from 'react-use'

import { logMessage } from '../../../../../../../Logging/log'
import { AudioLevelIndicator } from '../../../../../../../RoomUI/components/AudioLevelIndicator/AudioLevelIndicator'
import AspectContainer from '../../../../../../components/AspectContainer'
import { uploadGuestSpeakerPreview } from '../../../../../../object-asset-api'
import {
  JoinToSpeakIDSearchParamName,
  JoinToSpeakPresenterTokenSearchParamName,
} from '../../../../../../join-to-speak'

interface JoinToSpeakPreviewProps {
  sessionId: string
  isRequestingAccess: boolean
  audienceVideoTrack: MediaStreamTrack | null
  audienceAudioTrack: MediaStreamTrack | null
}

const JoinToSpeakPreview = ({
  sessionId,
  isRequestingAccess,
  audienceVideoTrack,
  audienceAudioTrack,
}: JoinToSpeakPreviewProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const intervalTimerRef = useRef<number | null>(null)
  const joinToSpeakID = useSearchParam(JoinToSpeakIDSearchParamName)
  const joinToSpeakPresenterToken = useSearchParam(
    JoinToSpeakPresenterTokenSearchParamName
  )

  useEffect(() => {
    const video = videoRef.current
    if (video && audienceVideoTrack) {
      video.srcObject = new MediaStream([audienceVideoTrack])
      video.autoplay = true
      video.play().catch((e: unknown) =>
        logMessage('Issue occurred while previewing video in JoinToSpeak.', {
          level: 'error',
          extra: { cause: e },
        })
      )
    }
  }, [audienceVideoTrack])

  const getBlobFromUserMedia = useCallback(async () => {
    const imageWidth = videoRef.current?.videoWidth ?? 640
    const imageHeight = videoRef.current?.videoHeight ?? 480

    if ('ImageCapture' in window) {
      // @ts-expect-error — experimental: https://developer.mozilla.org/en-US/docs/Web/API/ImageCapture
      const imageCapture = new window.ImageCapture(audienceVideoTrack)
      return imageCapture.takePhoto({ imageWidth, imageHeight })
    }

    return new Promise<Blob | null>((resolve, reject) => {
      if (!videoRef.current) {
        reject(new Error('Video element is not available'))
        return
      }

      const video = videoRef.current
      const canvas = document.createElement('canvas')
      canvas.width = imageWidth
      canvas.height = imageHeight
      const context = canvas.getContext('2d')

      try {
        context?.drawImage(video, 0, 0, imageWidth, imageHeight)
        canvas.toBlob(resolve, 'image/png')
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)))
      }
    })
  }, [audienceVideoTrack])

  useEffect(() => {
    let cancelled = false

    const startInterval = async () => {
      const handleInterval = async () => {
        if (
          audienceVideoTrack?.readyState !== 'live' ||
          !audienceVideoTrack.enabled ||
          audienceVideoTrack.muted ||
          joinToSpeakID === null ||
          joinToSpeakPresenterToken === null
        ) {
          return
        }

        // `ImageCapture.prototype.takePhoto()` sometimes throws because we are
        // calling it in an interval and the camera device is not ready.
        try {
          const blob = await getBlobFromUserMedia()
          if (!blob) return
          await uploadGuestSpeakerPreview({
            objectId: sessionId,
            joinToSpeakID,
            presenterToken: joinToSpeakPresenterToken,
            blob,
          })
        } catch (error) {
          logMessage('Failed to upload JoinToSpeak camera preview', {
            level: 'error',
            extra: {
              reason: error,
            },
          })
        }
      }

      try {
        await handleInterval()
      } finally {
        if (!cancelled) {
          intervalTimerRef.current = window.setInterval(handleInterval, 10000)
        }
      }
    }

    if (isRequestingAccess) {
      void startInterval()
    }

    return () => {
      cancelled = true
      if (intervalTimerRef.current !== null) {
        clearInterval(intervalTimerRef.current)
        intervalTimerRef.current = null
      }
    }
  }, [
    isRequestingAccess,
    sessionId,
    joinToSpeakID,
    joinToSpeakPresenterToken,
    getBlobFromUserMedia,
    audienceVideoTrack?.readyState,
    audienceVideoTrack?.enabled,
    audienceVideoTrack?.muted,
  ])

  return (
    <div className="media-previewer">
      <AspectContainer>
        {/* oxlint-disable-next-line jsx-a11y/media-has-caption */}
        <video ref={videoRef} />
      </AspectContainer>
      <AudioLevelIndicator audioTrack={audienceAudioTrack || undefined} />
    </div>
  )
}

export { JoinToSpeakPreview }
